import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TablePagination,
} from "@mui/material";
import { Column, Pagination } from "src/api/types";
import { ROWS_PER_PAGE } from "src/constants/rowPerPage";
import TableHeaderCell from "src/shared/TableHeaderCell";
import DataTableRow from "../DataTableRow";
import { useCallback, useMemo } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type Props<T> =
  | {
      primaryKey: keyof T;
      data: T[];
      columns: Column<T>[];
      onReorder: (rows: T[]) => void;
      height?: number | string;
      pagination?: never;
      total?: never;
    }
  | {
      primaryKey: keyof T;
      data: T[];
      columns: Column<T>[];
      onReorder: (rows: T[]) => void;
      height?: number | string;
      pagination: Pagination;
      total: number;
    };

function SortableDataTable<T>({
  data,
  columns,
  height = "100%",
  primaryKey,
  pagination,
  total,
  onReorder,
}: Props<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const ids = useMemo(
    () => data.map((r) => String(r[primaryKey])),
    [data, primaryKey],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));

      const reorderedRows = arrayMove(data, oldIndex, newIndex);

      // setRows(reorderedRows);
      onReorder(reorderedRows);
    },
    [data, ids, onReorder],
  );

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={closestCenter}
    >
      <Box>
        <TableContainer
          sx={{
            height,
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableHeaderCell key={column.id} column={column} />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? (
                data.map((row) => (
                  <SortableContext
                    items={ids}
                    strategy={verticalListSortingStrategy}
                    key={row[primaryKey] as unknown as string}
                  >
                    <DataTableRow
                      isDraggable={true}
                      id={String(row[primaryKey])}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                    >
                      {columns.map((column) => (
                        <TableCell
                          width={column.minWidth}
                          key={column.id}
                          align={column.align}
                        >
                          {column.render?.(row)}
                        </TableCell>
                      ))}
                    </DataTableRow>
                  </SortableContext>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={columns.length}>
                    <Typography>No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination != null ? (
          <TablePagination
            sx={{ height: 52 }}
            rowsPerPageOptions={ROWS_PER_PAGE}
            component="div"
            count={total}
            rowsPerPage={pagination.limit}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        ) : null}
      </Box>
    </DndContext>
  );
}

export default SortableDataTable;
